import {
  Component,
  DestroyRef,
  Input,
  OnInit,
  ChangeDetectorRef,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Categories } from 'src/app/models/categories';
import { Products } from 'src/app/models/Product.model';
import { PagesService } from 'src/app/services/pages.service';
import { ProductsService } from 'src/app/services/products.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * GestioneProdottiListComponent — main inventory page.
 *
 * Two view modes controlled by `viewMode`:
 *   • 'products'   — flat list of all products in a Diakonix table
 *                    with client-side filter + pagination, click-to-
 *                    detail-modal.
 *   • 'categories' — expandable cards: Family → Sub-family → Group →
 *                    Sub-group, each level navigates to a filtered
 *                    list (`gestione-prodotti-dettagli`).
 *
 * Phase 1.5 / v2.7.0 — TS cleaned up + bug fixes from user test:
 *   • dropped ~15 console.log + kendo `GridDataResult` /
 *     `PageChangeEvent` / dead fields (assignOrderForm, pr_stock,
 *     dateBy, filters, etc.),
 *   • dropped `triggerProducts()` / `getSubFamilies()` (zero call
 *     sites),
 *   • unified `showDetails` / `showDetailsWithouthCat` into
 *     `openDetail()`,
 *   • SEARCH FIX (2026-06-11): swapped backend `searchProducts(term)`
 *     (which used SQL `LIKE` — `_` is a single-char wildcard,
 *     breaking searches like '_ASSC361') for a client-side filter on
 *     the already-loaded `stockProducts` array. The 10k product list
 *     fits comfortably in memory and the user-typed term now matches
 *     the literal string. Search is live as you type.
 *   • PAGINATION (2026-06-11): added client-side pagination to avoid
 *     scrolling through 10k+ rows. PAGE_SIZE = 50 per page. Matches
 *     the pattern used in agente-list / client-list / supplier list.
 */
@Component({
  selector: 'app-gestione-prodotti',
  templateUrl: './gestione-prodotti-list.component.html',
  styleUrls: ['./gestione-prodotti-list.component.scss'],
  standalone: false,
})


export class GestioneProdottiListComponent implements OnInit {

  private readonly stockSrv = inject(ProductsService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly pagesServ = inject(PagesService);
  private cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly closeSrv = inject(OpenModalService);

  @Input() opened = false;

  // ── Data ────────────────────────────────────────────────────────
  public stockProducts: Products.IProductDetail[] = [];
  public allCategories: Categories.ICategory[] = [];
  public categories: Categories.Family[] = [];
  public filteredCategories: Categories.Family[] | null = null;

  // ── UI state ────────────────────────────────────────────────────
  /** 'products' = flat table, 'categories' = expandable cards. */
  public viewMode: 'products' | 'categories' = 'products';
  public filterTerm = '';
  public loading = false;
  public isSearched = false;

  // ── Pagination (Vista Prodotti) ────────────────────────────────
  public currentPage = 0;
  static readonly PAGE_SIZE = 50;

  // ── Sort state ────────────────────────────────────────────────────
  sortField = '';
  sortDir: 'asc' | 'desc' = 'asc';

  /** Selected product id forwarded to the detail modal (Chunk B.3). */
  public clickedProductId: string | null = null;

  // ──────────────────────────────────────────────────────────────────
  // Lifecycle.
  // ──────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loading = true;
    this.getCategories();
    this.activatedRoute.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const family = params.get('family');
        const subFamily = params.get('subFamily');
        const group = params.get('group');
        const subGroup = params.get('subGroup');
        if (family || subFamily || group || subGroup) {
          this.loadData(family, subFamily, group, subGroup);
        } else {
          this.getStockProducts();
        }
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Derived state — filtered + paginated products.
  // ──────────────────────────────────────────────────────────────────

  /** Client-side filter on the already-loaded inventory. Matches the
   *  literal term against pr_code OR pr_description, case-insensitive.
   *  This is the fix for the SQL `_` wildcard bug. */
  get filteredProducts(): Products.IProductDetail[] {
    const q = this.filterTerm?.trim().toLowerCase() ?? '';
    if (!q) return this.stockProducts;
    return this.stockProducts.filter((p) => {
      const code = (p.pr_code ?? '').toString().toLowerCase();
      const desc = (p.pr_description ?? '').toString().toLowerCase();
      return code.includes(q) || desc.includes(q);
    });
  }

  /**
   * Colonne numeriche del template inventario. Il sort su queste colonne
   * DEVE fare numeric compare, non string — altrimenti "10" < "2"
   * (comparazione char-by-char, bug classico segnalato PM 2026-07-08).
   *
   * `committed` e `available` sono dichiarati `any` in `IProductDetails`
   * (models/Product.model.ts:249-250) perché il BE li restituisce
   * incoerenti: a volte number nativo, a volte stringa. Il template li
   * coerce con `+item.available` unary plus — il sort ora fa lo stesso.
   */
  private static readonly NUMERIC_FIELDS = ['pr_stock', 'committed', 'available', 'pr_qty'];

  /** Slice of filteredProducts for the current page, after sorting. */
  get pagedProducts(): Products.IProductDetail[] {
    const sorted = this.sortField
      ? [...this.filteredProducts].sort((a, b) => {
          const cmp = this.compareBy(a, b, this.sortField);
          return this.sortDir === 'asc' ? cmp : -cmp;
        })
      : this.filteredProducts;
    const start = this.currentPage * GestioneProdottiListComponent.PAGE_SIZE;
    return sorted.slice(start, start + GestioneProdottiListComponent.PAGE_SIZE);
  }

  /**
   * Compare due prodotti su `field`. Numeric-aware per campi quantità
   * (evita "10" < "2" da string compare), case-insensitive per stringhe.
   * Fallback `-Infinity` per undefined/null/malformed → li spinge al
   * fondo in asc, in cima in desc (comportamento naturale).
   */
  private compareBy(a: Products.IProductDetail, b: Products.IProductDetail, field: string): number {
    const rawA = (a as any)[field];
    const rawB = (b as any)[field];
    if (GestioneProdottiListComponent.NUMERIC_FIELDS.includes(field)) {
      const na = GestioneProdottiListComponent.toNumeric(rawA);
      const nb = GestioneProdottiListComponent.toNumeric(rawB);
      return na < nb ? -1 : na > nb ? 1 : 0;
    }
    const sa = (rawA ?? '').toString().toLowerCase();
    const sb = (rawB ?? '').toString().toLowerCase();
    return sa.localeCompare(sb, 'it');
  }

  /**
   * Coerce a possibly-messy value to a finite number.
   * - `null` / `undefined` / `''` → `-Infinity` (bottom in asc sort)
   * - Native `number` → passthrough
   * - Numeric string (`"100"`, `"1.234,56"`) → normalized IT format
   *   (dot as thousand sep, comma as decimal) → number
   * - NaN or other → `-Infinity`
   */
  private static toNumeric(v: any): number {
    if (v === null || v === undefined || v === '') return Number.NEGATIVE_INFINITY;
    if (typeof v === 'number') return Number.isFinite(v) ? v : Number.NEGATIVE_INFINITY;
    // Italian number format: strip thousand dots then swap comma to dot.
    const normalized = String(v).replace(/\./g, '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
  }

  get totalPages(): number {
    return Math.max(
      1,
      Math.ceil(
        this.filteredProducts.length /
          GestioneProdottiListComponent.PAGE_SIZE,
      ),
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // View mode toggle.
  // ──────────────────────────────────────────────────────────────────

  setView(mode: 'products' | 'categories'): void {
    this.viewMode = mode;
    this.currentPage = 0;
    if (mode === 'products' && this.stockProducts.length === 0) {
      this.getStockProducts();
    }
  }

  // ──────────────────────────────────────────────────────────────────
  // Data loaders.
  // ──────────────────────────────────────────────────────────────────

  private loadData(
    family?: string,
    subFamily?: string,
    group?: string,
    subGroup?: string,
  ): void {
    if (!family && !subFamily && !group && !subGroup) return;
    this.setLoading(true);
    this.stockSrv
      .getProductCategories(family, subFamily, group, subGroup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.stockProducts = res || [];
          this.currentPage = 0;
          this.cdr.detectChanges();
        },
        error: () => this.setLoading(false),
        complete: () => this.setLoading(false),
      });
  }

  getStockProducts(): void {
    this.setLoading(true);
    this.stockSrv
      .getProducts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Products.IProductDetail[]) => {
          this.stockProducts = data || [];
          this.currentPage = 0;
          this.cdr.detectChanges();
        },
        error: () => this.setLoading(false),
        complete: () => this.setLoading(false),
      });
  }

  getCategories(): void {
    this.stockSrv
      .getCategories()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;
        this.allCategories = result;
        this.categories = new Categories.Categories(result).families;
        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  // ──────────────────────────────────────────────────────────────────
  // Search — client-side, live as you type.
  // ──────────────────────────────────────────────────────────────────

  onSearchChange(): void {
    this.isSearched = !!this.filterTerm?.trim();
    // Reset to first page on every search change to avoid stranding
    // the user on an empty trailing page.
    this.currentPage = 0;

    // The categories view runs its own filter logic (matches against
    // family/subFamily descriptions, preserves the tree shape).
    if (this.viewMode === 'categories') {
      this.applyCategoryFilter();
    }
  }

  private applyCategoryFilter(): void {
    if (!this.filterTerm?.trim()) {
      this.filteredCategories = null;
      return;
    }
    this.filteredCategories = Categories.filterFamily(
      this.filterTerm,
      this.categories,
    ).filtered;
  }

  clearSearch(): void {
    this.filterTerm = '';
    this.isSearched = false;
    this.filteredCategories = null;
    this.currentPage = 0;
  }

  // ──────────────────────────────────────────────────────────────────
  // Pagination.
  // ──────────────────────────────────────────────────────────────────

  prevPage(): void {
    if (this.currentPage > 0) this.currentPage -= 1;
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) this.currentPage += 1;
  }

  // ──────────────────────────────────────────────────────────────────
  // Navigation: category click → filtered list.
  // ──────────────────────────────────────────────────────────────────

  goToProducts(
    family: string = 'A',
    subFamily: string = 'A',
    group: string = 'A',
    subGroup: string = 'A',
  ): void {
    this.router.navigate(
      [`./${family.trim()}/${subFamily.trim()}/${group.trim()}/${subGroup.trim()}`],
      { relativeTo: this.activatedRoute },
    );
  }

  // ──────────────────────────────────────────────────────────────────
  // Detail: click product → open Diakonix detail modal (Chunk B.3).
  // ──────────────────────────────────────────────────────────────────

  /** Unified detail open. Replaces showDetails / showDetailsWithouthCat
   *  (identical bodies in the legacy code). Slug + id are kept the
   *  same shape for backward-compat with any existing bookmark. */
  openDetail(productId: string, productDesc: string): void {
    const slug = this.pagesServ
      .replaceAll(productDesc?.trim() ?? '', ' ', '-');
    this.clickedProductId = productId;
    this.router.navigate([`${slug}-${productId.trim()}`], {
      relativeTo: this.activatedRoute,
    });
  }

  // ──────────────────────────────────────────────────────────────────
  // Helpers.
  // ──────────────────────────────────────────────────────────────────

  sort(field: string): void {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = 'asc';
    }
    this.currentPage = 0;
  }

  sortIcon(field: string): string {
    if (this.sortField !== field) return '⇅';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  private setLoading(value: boolean): void {
    this.loading = value;
    this.cdr.detectChanges();
  }

}
